// Quote wizard progress survives a closed tab for a while (couples come back days later), but only
// the answers that say nothing about who they are. Contact details stay in sessionStorage, so the next
// person on a shared computer never finds someone else's name, email or phone filled in.

export interface SavedProgress {
  step: number;
  /** Furthest step index the user has reached; steps up to here can be revisited from the stepper */
  furthest?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  /** When the draft was last written (epoch ms); absent on progress that was never saved */
  savedAt?: number;
}

/** How long a draft is offered back to a returning visitor */
export const DRAFT_TTL_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

// Kept tab-scoped; everything else in `data` is safe to keep on the device
const CONTACT_FIELDS = ['fullName', 'email', 'mobile'] as const;

export const storageKey = (mode?: string) => `casa-aragon-wizard:${mode ?? 'lead'}`;
export const contactStorageKey = (mode?: string) => `${storageKey(mode)}:contact`;

// Storage can be missing or throw (private mode, blocked site data); progress then just isn't kept
const read = <T>(storage: () => Storage, key: string): T | null => {
  try {
    const raw = storage().getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const write = (storage: () => Storage, key: string, value: unknown) => {
  try {
    storage().setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
};

const remove = (storage: () => Storage, key: string) => {
  try {
    storage().removeItem(key);
  } catch {
    // ignore
  }
};

const local = () => localStorage;
const session = () => sessionStorage;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const splitContact = (data: Record<string, any> = {}) => {
  const draft = { ...data };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contact: Record<string, any> = {};
  for (const field of CONTACT_FIELDS) {
    if (field in draft) {
      contact[field] = draft[field];
      delete draft[field];
    }
  }
  return { draft, contact };
};

const readRaw = (mode: string | undefined): SavedProgress | null => {
  const draft = read<SavedProgress>(local, storageKey(mode));
  const contact = read<Record<string, unknown>>(session, contactStorageKey(mode));
  if (!draft || typeof draft.step !== 'number') {
    // A contact-only seed (e.g. the email typed on the login page) still pre-fills the wizard
    return contact ? { step: 0, data: contact } : null;
  }
  if (typeof draft.savedAt === 'number' && Date.now() - draft.savedAt > DRAFT_TTL_DAYS * DAY_MS) {
    remove(local, storageKey(mode));
    return contact ? { step: 0, data: contact } : null;
  }
  return { ...draft, data: { ...(draft.data ?? {}), ...(contact ?? {}) } };
};

export const loadProgress = (mode: string | undefined, stepCount: number): SavedProgress | null => {
  const parsed = readRaw(mode);
  if (!parsed || parsed.step < 0 || parsed.step >= stepCount) return null;
  return parsed;
};

export const saveProgress = (mode: string | undefined, progress: SavedProgress) => {
  const { draft, contact } = splitContact(progress.data);
  write(local, storageKey(mode), { step: progress.step, furthest: progress.furthest, data: draft, savedAt: Date.now() });
  if (Object.keys(contact).length) write(session, contactStorageKey(mode), contact);
  else remove(session, contactStorageKey(mode));
};

export const clearProgress = (mode?: string) => {
  remove(local, storageKey(mode));
  remove(session, contactStorageKey(mode));
};

/**
 * A draft worth offering back: the visitor got past the first step. Opening the wizard alone saves
 * the preselected guest count, which shouldn't turn the landing CTA into "Continuar".
 */
export const hasResumableProgress = (mode?: string): boolean => {
  const saved = readRaw(mode);
  return !!saved && Math.max(saved.step, saved.furthest ?? 0) > 0;
};

/** Whole days since the draft was last saved, or null without a timestamp (analytics only) */
export const daysSinceSaved = (progress: SavedProgress | null): number | null =>
  typeof progress?.savedAt === 'number' ? Math.floor((Date.now() - progress.savedAt) / DAY_MS) : null;

/**
 * Pre-fills answers before the wizard opens (e.g. an unregistered email typed on the login page),
 * keeping any progress the visitor already has.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const seedWizardData = (mode: string | undefined, data: Record<string, any>) => {
  const existing = readRaw(mode);
  saveProgress(mode, {
    step: existing?.step ?? 0,
    furthest: existing?.furthest,
    data: { ...(existing?.data ?? {}), ...data },
  });
};
